import React, { useEffect, useState } from 'react';
import { getLicenses, getPackages, uploadPackage, deletePackage, downloadPackage } from '../../services/api';

export default function PackagesPage() {
  const [packages, setPackages] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ version: '', releaseNotes: '', licenseId: '', environment: '', idempotent: true });
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');

  const load = () => {
    getPackages().then(setPackages);
    getLicenses().then(setLicenses);
  };
  useEffect(() => { load(); }, []);

  const handleUpload = async e => {
    e.preventDefault();
    if (!file) { setError('Please select a file'); return; }
    setError('');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('version', form.version);
      if (form.releaseNotes) fd.append('releaseNotes', form.releaseNotes);
      if (form.licenseId) fd.append('licenseId', form.licenseId);
      if (form.environment) fd.append('environment', form.environment);
      fd.append('idempotent', String(form.idempotent));
      await uploadPackage(fd);
      setShowForm(false);
      setForm({ version: '', releaseNotes: '', licenseId: '', environment: '', idempotent: true });
      setFile(null);
      load();
    } catch (err) {
      setError('Upload failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async id => {
    if (!window.confirm('Delete this package?')) return;
    await deletePackage(id);
    load();
  };

  const handleDownload = async (id, fileName) => {
    const blob = await downloadPackage(id);
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const formatSize = bytes => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h1 style={{ margin: 0 }}>Update Packages</h1>
        <button onClick={() => setShowForm(!showForm)} style={primaryBtn}>
          {showForm ? '✕ Cancel' : '+ Upload Package'}
        </button>
      </div>

      {/* Upload form */}
      {showForm && (
        <form onSubmit={handleUpload} style={{ ...card, marginBottom: '1.5rem' }}>
          <div style={{ fontWeight: 600, color: '#6b7280', marginBottom: '1rem' }}>New Package</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>

            {/* File drop zone */}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>File</label>
              <div
                style={{ ...dropZone, ...(file ? { borderColor: '#1a7f37' } : {}) }}
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); setFile(e.dataTransfer.files[0]); }}>
                {file
                  ? <span style={{ color: '#1a7f37' }}>✓ {file.name} ({formatSize(file.size)})</span>
                  : <span style={{ color: '#8a8f99' }}>Drag & drop or <label style={{ color: '#005cc5', cursor: 'pointer' }}>
                      browse<input type="file" style={{ display: 'none' }} onChange={e => setFile(e.target.files[0])} />
                    </label></span>}
              </div>
            </div>

            <div>
              <label style={labelStyle}>Version (semver)</label>
              <input style={inputStyle} placeholder="1.2.3" value={form.version}
                     onChange={e => setForm({ ...form, version: e.target.value })} required
                     pattern="\d+\.\d+\.\d+" title="Format: 1.2.3" />
            </div>

            <div>
              <label style={labelStyle}>Target (leave empty for global)</label>
              <select style={inputStyle} value={form.licenseId}
                      onChange={e => setForm({ ...form, licenseId: e.target.value })}>
                <option value="">🌍 Global (all clients)</option>
                {licenses.map(l => (
                  <option key={l.id} value={l.id}>{l.clientName} — {l.licenseKey}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={labelStyle}>Environnement (canal)</label>
              <select style={inputStyle} value={form.environment}
                      onChange={e => setForm({ ...form, environment: e.target.value })}>
                <option value="">Tous les environnements</option>
                {['INTEGRATION', 'VALIDATION', 'PREPROD', 'PROD', 'TEST'].map(env => (
                  <option key={env} value={env}>{env}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={labelStyle}>
                <input type="checkbox" checked={form.idempotent}
                       onChange={e => setForm({ ...form, idempotent: e.target.checked })}
                       style={{ marginRight: '0.5rem' }} />
                Idempotent (ré-applicable sans risque)
              </label>
              <div style={{ fontSize: '0.75rem', color: '#8a8f99', marginTop: '0.25rem' }}>
                Décoché : jamais redistribué automatiquement à une instance qui l'a déjà tenté.
              </div>
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Release Notes</label>
              <textarea style={{ ...inputStyle, height: '80px', resize: 'vertical' }}
                        placeholder="Describe what's new in this version..."
                        value={form.releaseNotes}
                        onChange={e => setForm({ ...form, releaseNotes: e.target.value })} />
            </div>
          </div>

          {error && <p style={{ color: '#c62828', fontSize: '0.875rem', marginTop: '0.5rem' }}>{error}</p>}

          <div style={{ textAlign: 'right', marginTop: '1rem' }}>
            <button type="submit" disabled={uploading} style={primaryBtn}>
              {uploading ? 'Uploading...' : '⬆ Upload'}
            </button>
          </div>
        </form>
      )}

      {/* Packages list */}
      <div style={{ ...card, overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: '1080px', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ color: '#8a8f99', fontSize: '0.8rem', textTransform: 'uppercase', borderBottom: '1px solid #cdcdcd' }}>
              {['Version', 'File', 'Size', 'Target', 'Env', 'Téléchargé', 'Release Notes', 'Uploaded', ''].map(h =>
                <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left' }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {packages.map(pkg => (
              <tr key={pkg.id} style={{ borderBottom: '1px solid #ececec' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>
                <td style={td}>
                  <span style={{ background: '#232853', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.82rem' }}>
                    v{pkg.version}
                  </span>
                </td>
                <td style={{ ...td, fontFamily: 'monospace', fontSize: '0.82rem', color: '#6b7280' }}>{pkg.fileName}</td>
                <td style={{ ...td, color: '#6b7280', fontSize: '0.82rem' }}>{formatSize(pkg.fileSize)}</td>
                <td style={td}>
                  {pkg.clientName
                    ? <span style={{ color: '#5f37a8' }}>👤 {pkg.clientName}</span>
                    : <span style={{ color: '#1a7f37' }}>🌍 Global</span>}
                </td>
                <td style={td}>
                  {pkg.environment
                    ? <span style={{ background: '#cdcdcd', color: '#555555', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem' }}>{pkg.environment}</span>
                    : <span style={{ color: '#8a8f99', fontSize: '0.82rem' }}>tous</span>}
                  {pkg.idempotent === false &&
                    <span title="Non idempotent : jamais redistribué automatiquement après une tentative"
                          style={{ marginLeft: '0.4rem', background: '#fdeee0', color: '#b45309', padding: '2px 6px', borderRadius: '4px', fontSize: '0.7rem' }}>⚠ non idem.</span>}
                </td>
                <td style={{ ...td, fontSize: '0.8rem', color: '#6b7280' }}
                    title={pkg.lastDownloadedAt ? 'Dernier : ' + new Date(pkg.lastDownloadedAt).toLocaleString('fr-FR') : ''}>
                  {pkg.downloadCount > 0
                    ? <>{pkg.downloadCount}×<br /><span style={{ fontSize: '0.72rem' }}>{new Date(pkg.lastDownloadedAt).toLocaleString('fr-FR')}</span></>
                    : '—'}
                </td>
                <td style={{ ...td, maxWidth: '250px' }}>
                  <span style={{ color: '#6b7280', fontSize: '0.82rem', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={pkg.releaseNotes}>
                    {pkg.releaseNotes || '—'}
                  </span>
                </td>
                <td style={{ ...td, fontSize: '0.82rem', color: '#8a8f99' }}>
                  {new Date(pkg.uploadedAt).toLocaleDateString('fr-FR')}<br/>
                  <span style={{ fontSize: '0.75rem' }}>{pkg.uploadedBy}</span>
                </td>
                <td style={{ ...td, display: 'flex', gap: '0.5rem' }}>
                  <button onClick={() => handleDownload(pkg.id, pkg.fileName)} style={linkBtn}>⬇ Download</button>
                  <button onClick={() => handleDelete(pkg.id)} style={dangerBtn}>✕</button>
                </td>
              </tr>
            ))}
            {packages.length === 0 && (
              <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: '#8a8f99', padding: '3rem' }}>
                No packages yet — upload your first package
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const card = { background: '#ffffff', borderRadius: '12px', padding: '1.5rem' };
const td = { padding: '0.85rem 1rem', color: '#444444' };
const labelStyle = { display: 'block', color: '#6b7280', fontSize: '0.8rem', marginBottom: '4px' };
const inputStyle = { width: '100%', padding: '0.6rem', background: '#f5f5f5', border: '1px solid #cdcdcd', color: '#444444', borderRadius: '6px', boxSizing: 'border-box', fontSize: '0.9rem' };
const dropZone = { border: '2px dashed #cdcdcd', borderRadius: '8px', padding: '1.5rem', textAlign: 'center', cursor: 'pointer', transition: 'border-color 0.2s' };
const primaryBtn = { padding: '0.5rem 1.25rem', background: '#232853', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem' };
const linkBtn = { padding: '0.3rem 0.75rem', background: 'transparent', border: '1px solid #cdcdcd', color: '#005cc5', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem' };
const dangerBtn = { padding: '0.3rem 0.75rem', background: '#fdecea', border: '1px solid #f3b8b4', color: '#b3261e', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem' };
